import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { StickyNote, X, Lock } from 'lucide-react';
import {
  Field,
  TextInput,
  TextareaInput,
  FormSection,
  PrimaryButton,
  GhostButton,
} from '../ui/Field';
import type { Annotation } from '../../types/api';

// ─────────────────────────────────────────────────────────────
// AnnotationForm — Operations Deck modal (Variation A).
// Mirrors CompanyForm / SiteForm / UserForm / ToolForm shell.
// Sections:
//   01 · Identity         — short + long description
//   02 · Live preview     — read-only chip + tagline preview row
//
// "In use" annotations come down with `isInUse=true`; the form
// locks all inputs and shows a read-only banner so the user can
// still inspect but not edit (matches the v1 behavior).
// ─────────────────────────────────────────────────────────────

const emptyAnnotation: Partial<Annotation> = {
  shortDescription: '',
  longDescription: '',
};

export interface AnnotationErrors {
  shortDescription?: string;
  longDescription?: string;
}

function validate(form: Partial<Annotation>): AnnotationErrors {
  const e: AnnotationErrors = {};
  if (!form.shortDescription?.trim()) {
    e.shortDescription = 'Short description is required';
  } else if ((form.shortDescription ?? '').length > 24) {
    e.shortDescription = 'Keep it under 24 characters';
  }
  if (!form.longDescription?.trim()) {
    e.longDescription = 'Long description is required';
  }
  return e;
}

export interface AnnotationFormProps {
  open: boolean;
  editing: Annotation | null;
  saving: boolean;
  onCancel: () => void;
  onSave: (form: Partial<Annotation>) => Promise<void> | void;
}

export function AnnotationForm({
  open,
  editing,
  saving,
  onCancel,
  onSave,
}: AnnotationFormProps) {
  const [form, setForm] = useState<Partial<Annotation>>(emptyAnnotation);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<AnnotationErrors>({});

  const readOnly = !!editing?.isInUse;

  useEffect(() => {
    if (!open) return;
    setSubmitted(false);
    setErrors({});
    setForm(editing ? { ...editing } : { ...emptyAnnotation });
  }, [open, editing]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !saving) onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, saving, onCancel]);

  if (!open) return null;

  const set = (key: keyof Annotation | string, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const liveValidate = (next: Partial<Annotation>) => {
    if (!submitted) return;
    setErrors(validate(next));
  };

  const handleSubmit = () => {
    const errs = validate(form);
    setErrors(errs);
    setSubmitted(true);
    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      const el = document.querySelector<HTMLElement>(`[data-field="${firstKey}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    void onSave(form);
  };

  const subtitle = editing
    ? `Annotations · Editing #${editing.id ?? ''}`
    : 'Annotations · New entry';
  const title = editing ? 'Edit annotation' : 'New annotation';
  const errorCount = Object.keys(errors).length;
  const shortPreview = (form.shortDescription || '—').toUpperCase();
  const longPreview =
    form.longDescription || 'A short, scannable explanation of what this marker represents.';

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="annotation-form-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(15, 23, 32, 0.45)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '5vh 16px 5vh',
        overflowY: 'auto',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onCancel();
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 620,
          background: 'var(--color-panel)',
          borderRadius: 14,
          boxShadow: '0 32px 80px rgba(0,0,0,0.30)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
        }}
      >
        {/* ── Header ────────────────────────────────────────── */}
        <header
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            padding: '20px 24px 18px',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', minWidth: 0 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 9,
                background: 'rgba(11,74,111,0.10)',
                color: 'var(--color-accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginTop: 2,
              }}
            >
              <StickyNote size={20} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 10.5,
                  textTransform: 'uppercase',
                  letterSpacing: 1.4,
                  color: 'var(--color-text-dim)',
                  fontFamily: 'var(--font-mono)',
                  fontWeight: 500,
                }}
              >
                {subtitle}
              </div>
              <h2
                id="annotation-form-title"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 24,
                  fontWeight: 500,
                  letterSpacing: -0.4,
                  color: 'var(--color-text)',
                  marginTop: 3,
                  marginBottom: 4,
                  lineHeight: 1.2,
                }}
              >
                {title}
              </h2>
              <p
                style={{
                  fontSize: 13,
                  color: 'var(--color-text-mid)',
                  margin: 0,
                  lineHeight: 1.45,
                  maxWidth: 480,
                }}
              >
                Reusable depth marker. Fields marked with{' '}
                <span style={{ color: 'var(--color-bad)' }}>*</span> are required.
              </p>
            </div>
          </div>

          <button
            onClick={onCancel}
            aria-label="Close"
            disabled={saving}
            style={{
              width: 30,
              height: 30,
              borderRadius: 6,
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-dim)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-panel-alt)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <X size={18} />
          </button>
        </header>

        {/* ── Read-only banner (when annotation is in use) ──── */}
        {readOnly && (
          <div
            style={{
              padding: '10px 24px',
              background: 'rgba(183,121,31,0.08)',
              borderBottom: '1px solid rgba(183,121,31,0.20)',
              color: 'var(--color-warn)',
              fontSize: 12.5,
              fontFamily: 'var(--font-body)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Lock size={14} />
            <strong style={{ fontWeight: 600 }}>Read-only</strong>
            <span style={{ color: 'var(--color-text-mid)' }}>
              — this annotation is linked to one or more wells and cannot be modified.
            </span>
          </div>
        )}

        {/* ── Error summary ─────────────────────────────────── */}
        {!readOnly && submitted && errorCount > 0 && (
          <div
            role="alert"
            style={{
              padding: '10px 24px',
              background: 'rgba(185,28,28,0.06)',
              borderBottom: '1px solid rgba(185,28,28,0.15)',
              color: 'var(--color-bad)',
              fontSize: 12.5,
              fontFamily: 'var(--font-body)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <strong style={{ fontWeight: 600 }}>
              {errorCount} field{errorCount === 1 ? '' : 's'} need
              {errorCount === 1 ? 's' : ''} attention
            </strong>
            <span style={{ color: 'var(--color-text-mid)' }}>— please review the highlighted inputs.</span>
          </div>
        )}

        {/* ── Scrollable body ───────────────────────────────── */}
        <div
          style={{
            padding: '22px 24px 24px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 26,
            background: 'var(--color-panel)',
          }}
        >
          {/* 01 Identity */}
          <FormSection number={1} title="Identity">
            <Field
              label="Short description"
              required
              error={submitted ? errors.shortDescription : undefined}
              hint="2–24 characters · used as the chip on logs (e.g. ICP, KOP, TD)"
            >
              <div data-field="shortDescription">
                <TextInput
                  value={form.shortDescription ?? ''}
                  placeholder="ICP"
                  readOnly={readOnly}
                  disabled={readOnly}
                  error={submitted && !!errors.shortDescription}
                  style={{ fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}
                  onChange={(e) => {
                    set('shortDescription', e.target.value);
                    liveValidate({ ...form, shortDescription: e.target.value });
                  }}
                />
              </div>
            </Field>

            <Field
              label="Long description"
              required
              error={submitted ? errors.longDescription : undefined}
              hint="Plain-language explanation that appears in tooltips and exports."
            >
              <div data-field="longDescription">
                <TextareaInput
                  value={form.longDescription ?? ''}
                  rows={4}
                  placeholder="Intermediate Casing Point — depth at which the intermediate string is set."
                  readOnly={readOnly}
                  disabled={readOnly}
                  error={submitted && !!errors.longDescription}
                  onChange={(e) => {
                    set('longDescription', e.target.value);
                    liveValidate({ ...form, longDescription: e.target.value });
                  }}
                />
              </div>
            </Field>
          </FormSection>

          {/* 02 Live preview */}
          <FormSection
            number={2}
            title="Live preview"
            description="How this annotation appears when attached to a well."
          >
            <div
              style={{
                padding: 14,
                background: 'var(--color-panel-alt)',
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
              }}
            >
              <div
                style={{
                  minWidth: 44,
                  padding: '4px 10px',
                  borderRadius: 6,
                  background: 'rgba(11,74,111,0.10)',
                  color: 'var(--color-accent)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11.5,
                  fontWeight: 600,
                  textAlign: 'center',
                  letterSpacing: 0.5,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {shortPreview}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text)' }}>
                  Annotation chip
                </div>
                <div
                  style={{
                    fontSize: 12.5,
                    color: 'var(--color-text-mid)',
                    marginTop: 3,
                    lineHeight: 1.45,
                  }}
                >
                  {longPreview}
                </div>
              </div>
            </div>
          </FormSection>
        </div>

        {/* ── Sticky footer ─────────────────────────────────── */}
        <footer
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '14px 24px',
            background: 'var(--color-panel-alt)',
            borderTop: '1px solid var(--color-border)',
          }}
        >
          <div
            style={{
              fontSize: 11.5,
              color: 'var(--color-text-dim)',
              fontFamily: 'var(--font-mono)',
              letterSpacing: 0.6,
            }}
          >
            {editing
              ? `LAST UPDATE · ${formatTs(editing.lastModifiedDate ?? editing.createdDate)}`
              : 'NEW RECORD'}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <GhostButton onClick={onCancel} disabled={saving}>
              {readOnly ? 'Close' : 'Cancel'}
            </GhostButton>
            {!readOnly && (
              <PrimaryButton onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Save annotation'}
              </PrimaryButton>
            )}
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

function formatTs(d?: string | null): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return d;
  }
}
